import '../../motion.css';
import './build.css';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import { buildExperienceCopy } from '@/i18n/ns/build-experience';
import { stagger, useEnterOnce } from '@/lib/motion';
import type { Field } from './vm';

/**
 * Items arriving while a build streams. Each item animates once, when its node is inserted;
 * keyed nodes already on screen never animate again, however often the VM updates. A batch
 * that arrives together staggers, capped so a long list never crawls in. Ids are the C2 node
 * ids, kept from pending to complete.
 *
 * `enterKey` names the stream (the job or revision): the items present on first sight rise
 * as one sequence, but revisiting the same stream (a tab switch, a remount) shows them still.
 */
export function StreamList<T>({
  items,
  id,
  render,
  enterKey,
  cap,
  flow = 'column',
  landing = false,
  className,
}: {
  items: readonly T[];
  id: (item: T) => string;
  render: (item: T) => ReactNode;
  enterKey: string;
  cap?: number;
  flow?: 'column' | 'row';
  /** Rows that land (imported rows, filled cells) get the soft highlight instead of a rise. */
  landing?: boolean | ((item: T) => boolean);
  className?: string;
}) {
  const firstSight = useEnterOnce(`stream:${enterKey}`);
  // Each key's place in the batch it arrived with, recorded after commit (never during render).
  const arrival = useRef(new Map<string, { order: number; initial: boolean }>());
  const keys = items.map(id);
  let fresh = 0;
  const placed = keys.map((key) => {
    const known = arrival.current.get(key);
    if (known) return { key, ...known };
    return { key, order: fresh++, initial: arrival.current.size === 0 };
  });
  useLayoutEffect(() => {
    for (const p of placed) if (!arrival.current.has(p.key)) arrival.current.set(p.key, { order: p.order, initial: p.initial });
  });
  return (
    <div className={className ? `pc-stream ${className}` : 'pc-stream'} data-flow={flow}>
      {items.map((item, i) => {
        const { key, order, initial } = placed[i];
        // On a revisit the items that were already there are still; later arrivals still move.
        const moves = !initial || firstSight;
        const lands = typeof landing === 'function' ? landing(item) : landing;
        const motionClass = lands && !initial ? 'pc-m-land' : 'pc-m-reveal';
        return (
          <div
            key={key}
            data-node-id={key}
            className={moves ? motionClass : undefined}
            style={moves ? stagger(order, { cap }) : undefined}
          >
            {render(item)}
          </div>
        );
      })}
    </div>
  );
}

/** The paragraph being written: its text so far, and a caret until it is done. */
export function StreamCaret({ text, done }: { text: string; done: boolean }) {
  return (
    <span className="pc-stream-caret">
      {text}
      {!done && <span className="pc-m-caret" aria-hidden="true" />}
    </span>
  );
}

export type PendingShapeKind = 'text' | 'value' | 'block' | 'inline';

/** Where something will be. A pending number is this shape, never 0. */
export function PendingShape({ shape = 'text', width }: { shape?: PendingShapeKind; width?: string }) {
  const copy = useNamespaceCopy(buildExperienceCopy);
  return (
    <span
      className="pc-pending"
      data-shape={shape}
      role="img"
      aria-label={copy.pendingValue}
      style={width ? { width } : undefined}
    />
  );
}

/**
 * One display field: its value when ready, a pending shape while it is pending, and a quiet
 * dash when it is unavailable (the viewer explains why; this never invents a value).
 */
export function FieldView<T>({
  field,
  format,
  shape = 'text',
}: {
  field: Field<T>;
  format: (value: T) => ReactNode;
  shape?: PendingShapeKind;
}) {
  const copy = useNamespaceCopy(buildExperienceCopy);
  if (field.status === 'ready') return <>{format(field.value)}</>;
  if (field.status === 'pending') return <PendingShape shape={shape} />;
  return (
    <span className="pc-build-meta" data-unavailable={field.reason} role="img" aria-label={copy.unavailableValue}>
      —
    </span>
  );
}
